import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api";
import BackButton from "../components/BackButton";
import Breadcrumbs from "../components/Breadcrumbs";
import FloatingInput from "../components/FloatingInput";
import Button from "../components/Button";
import ApiImage from "../components/ApiImage";
import Icon from "../lib/icon";
import { useToast } from "../contexts/ToastContext";
import "./scss/seller-market-edit.scss";
import SearchSelect from "../components/SearchSelect";
import FloatingTextarea from "../components/FloatingTextarea";

export default function SellerMarketEditPage() {
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [loading, setLoading] = useState(false);
  const [serverAvatar, setServerAvatar] = useState(null);
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [removeAvatar, setRemoveAvatar] = useState(false);
  const [market, setMarket] = useState(null);
  const [isActive, setIsActive] = useState(true);
  const [showTimePicker, setShowTimePicker] = useState(false);

  const [form, setForm] = useState({
    name: "",
    description: "",
    city: "",
    street: "",
    phone: "",
    workFrom: "09:00",
    workTo: "23:00"
  });

  useEffect(() => {
    load();
  }, []);

  async function load() {
    try {
      const { data } = await api.get("/api/seller/market");

      setMarket(data);
      setIsActive(data.isOpen);
      setServerAvatar(data.avatar || null);
      setForm({
        name: data.name || "",
        description: data.description || "",
        city: data.cityName || "",
        street: data.street || "",
        phone: data.phone || "",
        workFrom: data.workFrom || "09:00",
        workTo: data.workTo || "23:00"
      });
    } catch {
      showToast("Ошибка загрузки");
    }
  }

  const handleChange = (key, value) => {
    setForm(prev => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);

      if (avatarFile) {
        const formData = new FormData();
        formData.append("file", avatarFile);
        const { data } = await api.post(
          "/api/seller/market/avatar",
          formData,
          { headers: { "Content-Type": "multipart/form-data" } }
        );

        setAvatarPreview(data.image);
      }

      if (removeAvatar) {
        await api.delete("/api/seller/market/avatar");
      }

      await api.put("/api/seller/market", {
        ...form,
        isOpen: isActive
      });

      showToast("Изменения сохранены", "info");
      load();
      setAvatarPreview(null);
    } catch {
      showToast("Ошибка сохранения");
    } finally {
      setLoading(false);
    }
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setAvatarFile(file);
    setAvatarPreview(URL.createObjectURL(file));
    setRemoveAvatar(false);
  };

  const handleRemoveAvatar = () => {
    setAvatarFile(null);
    setAvatarPreview(null);
    setServerAvatar(null);
    setRemoveAvatar(true);
  };


  if (!market) return null;

  return (
    <div className="seller-market-edit">

      <BackButton />

      <Breadcrumbs
        items={[
          { label: "Главная", path: "/seller/dashboard" },
          { label: "Карточка магазина" }
        ]}
      />

      <h2>Карточка магазина</h2>

      {/* HERO */}
      <div className="seller-market-edit__hero">
        <div className="seller-market-edit__hero-left">
          <div className="seller-market-edit__avatar">
            {market.avatar
              ? <ApiImage src={market.avatar} />
              : <span>{form.name?.[0]}</span>}
          </div>

          <div>
            <div className="seller-market-edit__title">
              {form.name}
            </div>
            <div className="seller-market-edit__rating">
              <Icon name="star-filled" width={14} height={14} color="#FE9402" />
              {market.rating ?? 0}
              <span>{market.ratingCount ?? 0} оценок</span>
            </div>
          </div>
        </div>

        <div className="seller-market-edit__delivery">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M10.8333 2.5C10.373 2.5 9.99992 2.8731 9.99992 3.33333C9.99992 3.79357 10.373 4.16667 10.8333 4.16667H11.8493L12.1039 5.1852C12.1066 5.19698 12.1095 5.20866 12.1127 5.22025L13.2157 9.63242C11.6902 10.3116 10.5368 11.6751 10.145 13.3333C9.6768 13.3333 9.37234 13.3324 9.13978 13.3134C8.9134 13.2949 8.82946 13.2635 8.78826 13.2425C8.63146 13.1626 8.50398 13.0351 8.42408 12.8783C8.40309 12.8371 8.37169 12.7532 8.35319 12.5268C8.3339 12.2907 8.33325 11.9805 8.33325 11.5V8.33333C8.33325 7.8731 7.96016 7.5 7.49992 7.5H5.63217C4.96137 7.49999 4.40772 7.49998 3.95673 7.53683C3.48832 7.5751 3.05773 7.65724 2.65328 7.86331C2.02608 8.18289 1.51614 8.69282 1.19656 9.32003C0.990489 9.72448 0.908352 10.1551 0.870081 10.6235C0.833233 11.0745 0.833242 11.6281 0.833252 12.2989V14.1667C0.833252 14.6269 1.20635 15 1.66659 15H9.16659C9.20874 15 9.25015 14.9969 9.29062 14.9908C9.5398 15 9.82187 15 10.1345 15H10.8519C11.2832 15 11.6432 14.671 11.6819 14.2415C11.8541 12.331 13.4609 10.8333 15.4166 10.8333C16.3239 10.8333 17.154 11.1545 17.8026 11.6902C18.1575 11.9832 18.6827 11.9331 18.9758 11.5783C19.2689 11.2234 19.2188 10.6982 18.8639 10.4051C17.9276 9.63187 16.7253 9.16667 15.4166 9.16667C15.2167 9.16667 15.0194 9.17749 14.8252 9.19857L13.9839 5.83333H14.7659L14.8165 6.03545C14.9281 6.48194 15.3805 6.75341 15.827 6.64179C16.2735 6.53016 16.545 6.07772 16.4334 5.63122L16.2293 4.8148C16.2266 4.80302 16.2237 4.79134 16.2205 4.77975L16.0167 3.96455C15.9051 3.51806 15.4526 3.24659 15.0061 3.35821C14.6273 3.45293 14.3744 3.79303 14.3747 4.16667H13.5672L13.3084 3.13122C13.2156 2.76025 12.8823 2.5 12.4999 2.5H10.8333Z" fill="#5E3D44"/>
<path d="M8.09056 15.75H2.74261C3.19269 16.7801 4.22057 17.5 5.41659 17.5C6.61261 17.5 7.64049 16.7801 8.09056 15.75Z" fill="#5E3D44"/>
<path d="M15.4166 11.6667C13.8058 11.6667 12.4999 12.9725 12.4999 14.5833C12.4999 16.1942 13.8058 17.5 15.4166 17.5C17.0274 17.5 18.3333 16.1942 18.3333 14.5833C18.3333 12.9725 17.0274 11.6667 15.4166 11.6667Z" fill="#5E3D44"/>
</svg>
 45–55 мин с {form.workFrom} до {form.workTo}
        </div>

        <div className="seller-market-edit__description-preview">
          {form.description}
        </div>
      </div>

      {/* STATUS TOGGLE 
      <div className="seller-market-edit__toggle">
        <button
          className={isActive ? "active" : ""}
          onClick={() => setIsActive(true)}
        >
          Магазин активен
        </button>

        <button
          className={!isActive ? "active" : ""}
          onClick={() => setIsActive(false)}
        >
          Магазин не активен
        </button>
      </div>*/}

      {/* WORK TIME 
      <div className="seller-market-edit__worktime">
        <div>Время работы магазина</div>
        <div>
          {form.workFrom} – {form.workTo}
        </div>
      </div>*/}

      {/* AVATAR */}
      <div className="seller-market-edit__avatar-block">

        {!serverAvatar && !avatarPreview && (
          <label className="seller-market-edit__upload">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path fill-rule="evenodd" clip-rule="evenodd" d="M12 3C12.5523 3 13 3.44772 13 4V13.5858L14.2929 12.2929C14.6834 11.9024 15.3166 11.9024 15.7071 12.2929C16.0976 12.6834 16.0976 13.3166 15.7071 13.7071L12.7071 16.7071C12.3166 17.0976 11.6834 17.0976 11.2929 16.7071L8.2929 13.7071C7.90238 13.3166 7.90238 12.6834 8.2929 12.2929C8.68343 11.9024 9.31659 11.9024 9.70712 12.2929L11 13.5858V4C11 3.44772 11.4477 3 12 3ZM6.2875 8.99999C6.30256 9 6.31783 9 6.33334 9H7.16668C7.71896 9 8.16668 9.44772 8.16668 10C8.16668 10.5523 7.71896 11 7.16668 11H6.33334C6.00266 11 5.90172 11.001 5.8257 11.0076C4.8588 11.0922 4.09221 11.8588 4.00762 12.8257C4.00097 12.9017 4.00001 13.0027 4.00001 13.3333V15.2C4.00001 16.0566 4.00079 16.6389 4.03756 17.089C4.07338 17.5274 4.13831 17.7516 4.218 17.908C4.40974 18.2843 4.7157 18.5903 5.09203 18.782C5.24843 18.8617 5.47263 18.9266 5.91105 18.9624C6.36114 18.9992 6.94343 19 7.80001 19H16.2C17.0566 19 17.6389 18.9992 18.089 18.9624C18.5274 18.9266 18.7516 18.8617 18.908 18.782C19.2843 18.5903 19.5903 18.2843 19.782 17.908C19.8617 17.7516 19.9266 17.5274 19.9625 17.089C19.9992 16.6389 20 16.0566 20 15.2V13.3333C20 13.0027 19.9991 12.9017 19.9924 12.8257C19.9078 11.8588 19.1412 11.0922 18.1743 11.0076C18.0983 11.001 17.9974 11 17.6667 11H16.8333C16.2811 11 15.8333 10.5523 15.8333 10C15.8333 9.44772 16.2811 9 16.8333 9H17.6667C17.6822 9 17.6975 9 17.7125 8.99999C17.9775 8.99995 18.1736 8.99991 18.3486 9.01522C20.2824 9.18441 21.8156 10.7176 21.9848 12.6514C22.0001 12.8264 22.0001 13.0226 22 13.2875L22 15.2413C22 16.0463 22 16.7106 21.9558 17.2518C21.9099 17.8139 21.8113 18.3306 21.564 18.816C21.1805 19.5686 20.5686 20.1805 19.816 20.564C19.3306 20.8113 18.8139 20.9099 18.2518 20.9558C17.7106 21 17.0463 21 16.2413 21H7.75869C6.95373 21 6.28937 21 5.74818 20.9558C5.18609 20.9099 4.66938 20.8113 4.18405 20.564C3.4314 20.1805 2.81948 19.5686 2.43598 18.816C2.18869 18.3306 2.09013 17.8139 2.0442 17.2518C1.99999 16.7106 2 16.0463 2.00001 15.2413L2.00001 13.3333C2.00001 13.3178 2.00001 13.3026 2.00001 13.2875C1.99996 13.0226 1.99992 12.8264 2.01523 12.6514C2.18442 10.7176 3.7176 9.18441 5.65139 9.01522C5.82638 8.99991 6.02257 8.99995 6.2875 8.99999Z" fill="#A1233C" />
            </svg>
            Загрузить фотографию
            <input
              type="file"
              hidden
              accept="image/*"
              onChange={handleAvatarChange}
            />
          </label>
        )}

        {(serverAvatar) && (
          <div className="seller-market-edit__avatar-preview">
            <ApiImage
              src={avatarPreview || serverAvatar}
              alt=""
            />

            <button
              className="seller-market-edit__avatar-remove"
              onClick={handleRemoveAvatar}
            >
              ×
            </button>
          </div>
        )}

        {(avatarPreview) && (
          <div className="seller-market-edit__avatar-preview">
            <img
              src={avatarPreview}
              alt=""
            />

            <button
              className="seller-market-edit__avatar-remove"
              onClick={handleRemoveAvatar}
            >
              ×
            </button>
          </div>
        )}

      </div>

      {/* FORM */}
      <FloatingInput
        label="Название магазина"
        value={form.name}
        onChange={e => handleChange("name", e.target.value)}
      />

      <FloatingTextarea
        label="Описание"
        value={form.description}
        onChange={e => handleChange("description", e.target.value)}
      />

      <SearchSelect
        label="Город"
        value={form.city}
        onChange={val => handleChange("it", val)}
        options={[
          "Москва"
        ]}
        recent={[
          "Москва"
        ]}
      />

      <FloatingInput
        label="Улица, дом"
        value={form.street}
        onChange={e => handleChange("street", e.target.value)}
      />

      <FloatingInput
        label="Телефон магазина"
        value={form.phone}
        onChange={e => handleChange("phone", e.target.value)}
      />

      <div
        className={`floating-input time-range-input ${
          form.workFrom || form.workTo ? "active" : ""
        }`}
        onClick={() => setShowTimePicker(true)}
      >
        <input
          readOnly
          value={`${form.workFrom} — ${form.workTo}`}
        />
        <label>Время работы</label>
      </div>

      <Button
        className="btn-primary large"
        onClick={handleSubmit}
        load={loading}
      >
        Сохранить изменения
      </Button>
      {showTimePicker && (
        <div className="time-modal">
          <div className="time-modal__content">

            <div className="time-modal__header">
              <span>Время работы</span>
            </div>

            <div className="time-row">
              <span>Открытие</span>
              <input
                type="time"
                value={form.workFrom}
                onChange={e => handleChange("workFrom", e.target.value)}
              />
            </div>

            <div className="time-row">
              <span>Закрытие</span>
              <input
                type="time"
                value={form.workTo}
                onChange={e => handleChange("workTo", e.target.value)}
              />
            </div>

            <button
              className="time-modal__confirm"
              onClick={() => setShowTimePicker(false)}
            >
              Готово
            </button>

          </div>
        </div>
      )}
    </div>
  );
}
